import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  getDocs,
  updateDoc,
  writeBatch,
  doc,
  serverTimestamp 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

import { db,storage } from '../../config/firebase';
import { Loader, AlertCircle, Plus, X, Save, Trash } from 'lucide-react';
import RichTextEditor from './RichTextEditor';  // יש להוסיף את הייבוא
import { IconSelector, icons } from './IconSelector';
import * as LucideIcons from 'lucide-react';

// בקומפוננט SubSubjectItem
const SubSubjectItem = ({ subSubject, onChangeText, onChangeIcon, onRemove }) => {
  const [showIconSelector, setShowIconSelector] = useState(false);
  // בדיקה האם זה מהמבנה הישן או החדש
  const text = typeof subSubject === 'string' ? subSubject : subSubject.text;
  const icon = typeof subSubject === 'string' ? 'FileText' : subSubject.icon;
  const IconComponent = LucideIcons[icon];

  return (
    <div className="flex gap-3">
      <div className="relative">
        <button
          onClick={() => setShowIconSelector(!showIconSelector)}
          className="p-3 border rounded-lg hover:bg-gray-50 transition-all"
          title="בחר אייקון"
        >
          <IconComponent className="w-6 h-6 text-gray-700" />
        </button>
        
        {showIconSelector && (
          <IconSelector
            selectedIcon={icon}
            onSelect={(iconName) => {
              onChangeIcon(iconName);
              setShowIconSelector(false);
            }}
            onClose={() => setShowIconSelector(false)}
          />
        )}
      </div>
      
      <input
        type="text"
        value={text}
        onChange={(e) => onChangeText(e.target.value)}
        className="flex-1 p-2 border rounded-lg"
        placeholder="הזן שם תת נושא"
      />
      
      <button
        onClick={onRemove}
        className="text-red-600 hover:text-red-700 p-3 hover:bg-red-50 rounded-lg transition-colors"
      >
        <X className="w-6 h-6" />
      </button>
    </div>
  );
};

// קומפוננט לניהול תמונות
const ImageUploadSection = ({ label, currentImage, onImageChange, subjectId, imageType }) => {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <div className="flex gap-4 items-start">
        {currentImage && (
          <div className="relative w-48 h-32">
            <img 
              src={currentImage} 
              alt={label}
              className="w-full h-full object-cover rounded-lg"
            />
            <button
              onClick={() => onImageChange(subjectId, imageType, null)}
              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              title="מחק תמונה"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
        <div className="flex-1">
          <input
            type="file"
            accept="image/*"
            onChange={async (e) => {
              if (e.target.files?.[0]) {
                try {
                  const file = e.target.files[0];
                  console.log(`Uploading ${imageType}:`, file.name);
                  
                  const storageRef = ref(storage, `subjects/${subjectId}/${imageType}`);
                  await uploadBytes(storageRef, file);
                  console.log(`${imageType} uploaded successfully`);
                  
                  const url = await getDownloadURL(storageRef);
                  console.log(`Got download URL for ${imageType}:`, url);
                  
                  onImageChange(subjectId, imageType, url);
                  console.log(`${imageType} URL set in pending changes`);
                } catch (error) {
                  console.error(`Error uploading ${imageType}:`, error);
                }
              }
            }}
            className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-full file:border-0
              file:text-sm file:font-semibold
              file:bg-blue-50 file:text-blue-700
              hover:file:bg-blue-100"
          />
          <p className="mt-1 text-sm text-gray-500">
            העלה {label.toLowerCase()}
          </p>
        </div>
      </div>
    </div>
  );
};

const SubjectsPage = () => {
  const [subjects, setSubjects] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState({ show: false, message: '', type: '' });
  const [pendingChanges, setPendingChanges] = useState({});
  const [editingSubjectId, setEditingSubjectId] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      // טעינת נושאים
      const subjectsCollection = collection(db, 'subjects');
      const subjectsSnapshot = await getDocs(subjectsCollection);
      const subjectsData = subjectsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        subSubjects: doc.data().subSubjects || []
      }));
      setSubjects(subjectsData);

      // טעינת שאלות
      const questionsCollection = collection(db, 'questions');
      const questionsSnapshot = await getDocs(questionsCollection);
      const questionsData = questionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setQuestions(questionsData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('אירעה שגיאה בטעינת הנתונים');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // SubSubjects handlers
  const handleAddSubSubject = (subjectId) => {
    const currentSubSubjects = pendingChanges[subjectId]?.subSubjects || 
      subjects.find(s => s.id === subjectId).subSubjects || [];
    
    setPendingChanges(prev => ({
      ...prev,
      [subjectId]: {
        ...prev[subjectId],
        subSubjects: [...currentSubSubjects, { text: '', icon: 'FileText' }]
      }
    }));
  };

  const handleSubSubjectChange = (subjectId, index, field, value) => {
    const currentSubSubjects = pendingChanges[subjectId]?.subSubjects || 
      subjects.find(s => s.id === subjectId).subSubjects || [];
    
    const newSubSubjects = [...currentSubSubjects];
    if (typeof newSubSubjects[index] === 'string') {
      // המרה מהמבנה הישן למבנה החדש
      newSubSubjects[index] = { text: newSubSubjects[index], icon: 'FileText' };
    }
    newSubSubjects[index] = {
      ...newSubSubjects[index],
      [field]: value
    };
    
    setPendingChanges(prev => ({
      ...prev,
      [subjectId]: {
        ...prev[subjectId],
        subSubjects: newSubSubjects
      }
    }));
  };

  const handleRemoveSubSubject = (subjectId, index) => {
    const currentSubSubjects = pendingChanges[subjectId]?.subSubjects || 
      subjects.find(s => s.id === subjectId).subSubjects || [];
    
    const newSubSubjects = currentSubSubjects.filter((_, i) => i !== index);
    setPendingChanges(prev => ({
      ...prev,
      [subjectId]: {
        ...prev[subjectId],
        subSubjects: newSubSubjects
      }
    }));
  };

  // Subject handlers
  const handleSubjectChange = (subjectId, field, value) => {
    console.log('Setting pending change:', { subjectId, field, value });
    setPendingChanges(prev => {
      const newChanges = {
        ...prev,
        [subjectId]: {
          ...prev[subjectId],
          [field]: value
        }
      };
      console.log('New pending changes:', newChanges);
      return newChanges;
    });
  };

  // פונקציה מיוחדת לטיפול בתמונות
  const handleImageChange = (subjectId, imageType, url) => {
    handleSubjectChange(subjectId, imageType, url);
    if (!url) {
      // אם התמונה נמחקה, נציג הודעת הצלחה
      setSaveStatus({ show: true, message: 'התמונה נמחקה', type: 'info' });
      setTimeout(() => setSaveStatus({ show: false, message: '', type: '' }), 2000);
    }
  };

  // Q&A handlers
  const handleUpdateQA = (subjectId, questionId, updates) => {
    setPendingChanges(prev => {
      const currentPendingSubject = prev[subjectId] || {};
      const currentPendingQuestions = currentPendingSubject.pendingQuestions || {};
  
      return {
        ...prev,
        [subjectId]: {
          ...currentPendingSubject,
          pendingQuestions: {
            ...currentPendingQuestions,
            [questionId]: {
              ...(currentPendingQuestions[questionId] || {}),
              id: questionId,
              ...updates
            }
          }
        }
      };
    });
  };
  
  const handleAddQA = (subjectId) => {
    const newQuestionId = `new_${Date.now()}`;
    
    setPendingChanges(prev => {
      const currentPendingSubject = prev[subjectId] || {};
      const currentPendingQuestions = currentPendingSubject.pendingQuestions || {};
  
      return {
        ...prev,
        [subjectId]: {
          ...currentPendingSubject,
          pendingQuestions: {
            ...currentPendingQuestions,
            [newQuestionId]: {
              id: newQuestionId,
              subjectId,
              question: "",
              answer: "",
              isNew: true
            }
          }
        }
      };
    });
  };
  
  const handleRemoveQA = (subjectId, questionId) => {
    setPendingChanges(prev => {
      const currentPendingSubject = prev[subjectId] || {};
      const currentPendingQuestions = currentPendingSubject.pendingQuestions || {};
  
      const updatedPendingQuestions = { ...currentPendingQuestions };
      if (questionId.startsWith('new_')) {
        delete updatedPendingQuestions[questionId];
      } else {
        updatedPendingQuestions[questionId] = {
          ...(updatedPendingQuestions[questionId] || {}),
          id: questionId,
          isDeleted: true
        };
      }
  
      return {
        ...prev,
        [subjectId]: {
          ...currentPendingSubject,
          pendingQuestions: updatedPendingQuestions
        }
      };
    });
  };

  // בפונקציית handleSave
  const handleSave = async (subjectId) => {
    const pendingSubjectChanges = pendingChanges[subjectId];
    if (!pendingSubjectChanges) return;

    setEditingSubjectId(subjectId);
    setSaveStatus({ show: true, message: 'שומר שינויים...', type: 'info' });

    try {
      const batch = writeBatch(db);

      // עדכון נושא
      const subjectRef = doc(db, 'subjects', subjectId);
      const { pendingQuestions, ...subjectUpdates } = pendingSubjectChanges;
      
      const updates = {
        ...subjectUpdates,
        updatedAt: serverTimestamp()
      };

      batch.update(subjectRef, updates);

      // טיפול בשאלות ותשובות
      if (pendingQuestions) {
        for (const [questionId, questionData] of Object.entries(pendingQuestions)) {
          // אם זו שאלה חדשה
          if (questionData.isNew) {
            if (!questionData.isDeleted) {
              const newQuestionRef = doc(collection(db, 'questions'));
              batch.set(newQuestionRef, {
                subjectId,
                question: questionData.question || '',
                answer: questionData.answer || '',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
              });
            }
          }
          // אם זו שאלה קיימת
          else {
            const questionRef = doc(db, 'questions', questionId);
            if (questionData.isDeleted) {
              batch.delete(questionRef);
            } else {
              batch.update(questionRef, {
                question: questionData.question,
                answer: questionData.answer,
                updatedAt: serverTimestamp()
              });
            }
          }
        }
      }
      
      await batch.commit();
      
      // רענון הנתונים
      await fetchData();

      // ניקוי השינויים הממתינים
      setPendingChanges(prev => {
        const newChanges = { ...prev };
        delete newChanges[subjectId];
        return newChanges;
      });

      setSaveStatus({ show: true, message: 'נשמר בהצלחה!', type: 'success' });
      setTimeout(() => setSaveStatus({ show: false, message: '', type: '' }), 3000);
    } catch (err) {
      console.error('Error saving changes:', err);
      setSaveStatus({ show: true, message: 'שגיאה בשמירת הנתונים', type: 'error' });
    } finally {
      setEditingSubjectId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-red-500">
        <AlertCircle className="w-6 h-6 mr-2" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl md:text-2xl font-bold">ניהול תחומי התמחות</h1>
        {saveStatus.show && (
          <div className={`
            px-4 py-2 rounded-lg text-sm md:text-base
            ${saveStatus.type === 'success' ? 'bg-green-100 text-green-700' : ''}
            ${saveStatus.type === 'error' ? 'bg-red-100 text-red-700' : ''}
            ${saveStatus.type === 'info' ? 'bg-blue-100 text-blue-700' : ''}
          `}>
            {saveStatus.message}
          </div>
        )}
      </div>

      <div className="space-y-6">
        {subjects.map(subject => (
          <div key={subject.id} className="bg-white p-6 rounded-lg shadow-md">
            <div className="space-y-6">
              {/* כותרת תחום */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  שם התחום
                </label>
                <input
                  type="text"
                  value={pendingChanges[subject.id]?.name ?? subject.name}
                  onChange={(e) => handleSubjectChange(subject.id, 'name', e.target.value)}
                  className="w-full p-2 border rounded-md"
                />
              </div>

              {/* תיאור כללי - עם עורך טקסט עשיר */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  תיאור כללי
                </label>
                <RichTextEditor
                  content={pendingChanges[subject.id]?.generalContent ?? subject.generalContent}
                  onChange={(newContent) => handleSubjectChange(subject.id, 'generalContent', newContent)}
                />
              </div>

              {/* תמונות - 3 תמונות */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* תמונה ראשית */}
                <ImageUploadSection
                  label="תמונה ראשית"
                  currentImage={pendingChanges[subject.id]?.mainImage || subject.mainImage}
                  onImageChange={handleImageChange}
                  subjectId={subject.id}
                  imageType="mainImage"
                />

                {/* תמונה שנייה */}
                <ImageUploadSection
                  label="תמונה שנייה"
                  currentImage={pendingChanges[subject.id]?.secondImage || subject.secondImage}
                  onImageChange={handleImageChange}
                  subjectId={subject.id}
                  imageType="secondImage"
                />

                {/* תמונה שלישית */}
                <ImageUploadSection
                  label="תמונה שלישית"
                  currentImage={pendingChanges[subject.id]?.thirdImage || subject.thirdImage}
                  onImageChange={handleImageChange}
                  subjectId={subject.id}
                  imageType="thirdImage"
                />
              </div>

              {/* תתי נושאים */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    תתי נושאים
                  </label>
                  <button
                    onClick={() => handleAddSubSubject(subject.id)}
                    className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    <span>הוסף תת נושא</span>
                  </button>
                </div>
                <div className="space-y-2">
                  {(pendingChanges[subject.id]?.subSubjects || subject.subSubjects).map((subSubject, index) => (
                    <SubSubjectItem
                      key={index}
                      subSubject={subSubject}
                      onChangeText={(text) => handleSubSubjectChange(subject.id, index, 'text', text)}
                      onChangeIcon={(icon) => handleSubSubjectChange(subject.id, index, 'icon', icon)}
                      onRemove={() => handleRemoveSubSubject(subject.id, index)}
                    />
                  ))}
                </div>
              </div>

              {/* שאלות ותשובות */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    שאלות ותשובות
                  </label>
                  <button
                    onClick={() => handleAddQA(subject.id)}
                    className="text-blue-600 hover:text-blue-700 flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-md"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="text-sm">הוסף שאלה</span>
                  </button>
                </div>

                <div className="space-y-4">
                  {/* שאלות חדשות */}
                  {Object.entries(
                    (pendingChanges[subject.id]?.pendingQuestions || {})
                  ).map(([questionId, questionData]) => (
                    !questionData.isDeleted && questionData.isNew && (
                      <div 
                        key={questionId} 
                        className="bg-gray-50 rounded-lg p-4 shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-3"
                      >
                        <div className="flex-1 space-y-3">
                          <div>
                            <label className="text-xs text-gray-600 mb-1 block">שאלה</label>
                            <RichTextEditor
                              content={questionData.question}
                              onChange={(newContent) => handleUpdateQA(
                                subject.id, 
                                questionId, 
                                { question: newContent }
                              )}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 mb-1 block">תשובה</label>
                            <RichTextEditor
                              content={questionData.answer}
                              onChange={(newContent) => handleUpdateQA(
                                subject.id, 
                                questionId, 
                                { answer: newContent }
                              )}
                            />
                          </div>
                        </div>
                        
                        <div className="flex sm:flex-col gap-2 items-center justify-center">
                          <button 
                            onClick={() => handleRemoveQA(subject.id, questionId)}
                            className="text-red-500 hover:text-red-600 bg-red-50 p-2 rounded-full"
                            title="מחק שאלה"
                          >
                            <Trash className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )
                  ))}

                  {/* שאלות קיימות */}
                  {questions
                    .filter(q => 
                      q.subjectId === subject.id && 
                      !pendingChanges[subject.id]?.pendingQuestions?.[q.id]?.isDeleted
                    )
                    .map(question => (
                      <div 
                        key={question.id} 
                        className="bg-gray-50 rounded-lg p-4 shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-3"
                      >
                        <div className="flex-1 space-y-3">
                          <div>
                            <label className="text-xs text-gray-600 mb-1 block">שאלה</label>
                            <RichTextEditor
                              content={
                                pendingChanges[subject.id]?.pendingQuestions?.[question.id]?.question ?? 
                                question.question
                              }
                              onChange={(newContent) => handleUpdateQA(
                                subject.id, 
                                question.id, 
                                { question: newContent }
                              )}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 mb-1 block">תשובה</label>
                            <RichTextEditor
                              content={
                                pendingChanges[subject.id]?.pendingQuestions?.[question.id]?.answer ?? 
                                question.answer
                              }
                              onChange={(newContent) => handleUpdateQA(
                                subject.id, 
                                question.id, 
                                { answer: newContent }
                              )}
                            />
                          </div>
                        </div>
                        
                        <div className="flex sm:flex-col gap-2 items-center justify-center">
                          <button 
                            onClick={() => handleRemoveQA(subject.id, question.id)}
                            className="text-red-500 hover:text-red-600 bg-red-50 p-2 rounded-full"
                            title="מחק שאלה"
                          >
                            <Trash className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* כפתור שמירה */}
              {pendingChanges[subject.id] && (
                <button
                  onClick={() => handleSave(subject.id)}
                  disabled={editingSubjectId === subject.id}
                  className={`
                    w-full md:w-auto px-6 py-2 rounded-md
                    flex items-center justify-center space-x-2 space-x-reverse
                    ${editingSubjectId === subject.id 
                      ? 'bg-blue-300 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                    }
                    text-white font-medium transition-colors
                  `}
                >
                  <Save className="w-4 h-4" />
                  <span>{editingSubjectId === subject.id ? 'שומר...' : 'שמור שינויים'}</span>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SubjectsPage;